/* eslint-disable sort-keys-plus/sort-keys */
import DatePicker from '@/components/date-picker/DatePicker';
import { GLOBAL_SEARCH_RESULT_PLANS_TYPE } from '@/components/main-layout/GlobalSearch/GlobalSearchResults/constants';
import { useSnackbar } from '@/contexts/SnackBarContext';
import {
  AmendmentsBilling,
  AmendmentsProvisions
} from '@/models/Amendments.model';
import { GlobalSearchPlanDto } from '@/models/GlobalSearchDTO.model';
import {
  AmendmentsService,
  CreateAmendmentsRecordDto
} from '@/services/Amendments.service';
import GlobalSearchService from '@/services/GlobalSearch.service';
import { Search } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Unstable_Grid2 as Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import dayjs from 'dayjs';
import { Field, Form, Formik } from 'formik';
import React, { FC, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { useDebounce } from 'use-debounce';
import * as yup from 'yup';

import { PlanInputSearch } from '../../../platform-reporting/components/PlanInputSearch';
import { defaultFilters } from '../AmendmentsPage.route';
import { SelectedPlanCard } from './SelectedPlanCard.component';

export enum AmendmentStatus {
  NEW_REQUESTS = 'New Requests - Approval Needed',
  IN_PROGRESS = 'In Progress - Draft, Review, Send',
  PENDING = 'Pending Jira Ticket',
  VESTING = 'Vesting - Onboarding',
  AWAITING_SIGNATURE = 'Awaiting Signature',
  FINAL_REVIEW_NEEDED = 'Final Review Needed',
  AWAITING_PLAN_SPEC_UPDATES = 'Awaiting Plan Spec Updates',
  COMPLETED = 'Completed',
  CANCELLED_OR_REJECTED = 'Cancelled or Rejected'
}

type CreateNewAmendmentDialogProps = {
  close: () => void;
  updatedFilters: Record<string, any>;
};

const copyStyling = {
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  display: '-webkit-box',
  marginLeft: '20px'
};

export const createAmendmentValidationSchema = yup.object({
  planId: yup.string().nullable().required('Plan ID is required'),
  provisions: yup
    .array()
    .of(yup.string().required())
    .min(1, 'At least one provision is required')
    .required('Provisions are required'),
  desiredEffectiveDate: yup
    .string()
    .required('Desired Effective Date is required'),
  changeRequestNotes: yup.object({
    currentProvision: yup.string(),
    requestedProvision: yup.string(),
    notes: yup.string(),
    intentionOfChange: yup.string(),
    affectedParty: yup.string()
  }),
  isPsaChangeNeeded: yup.boolean(),
  billable: yup.string().required(),
  billing: yup.string().required('Billing information is required'),
  isFeeWaiverCompleted: yup.boolean()
});

export const CreateNewAmendmentDialog: FC<
  CreateNewAmendmentDialogProps
> = props => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [isTextExpanded, toggleIsTextExpanded] = useToggle(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [plan, setPlan] = useState<GlobalSearchPlanDto | null>(null);
  const [hasSelectedPlan, toggleHasSelectedPlan] = useToggle(!!plan);
  const [isBillable, setIsBillable] = useState<boolean>(null);
  const billableAmountSelections = useMemo(() => {
    return isBillable
      ? [
          AmendmentsBilling.DISCOUNTED_FEE,
          AmendmentsBilling.GENERAL_AMENDMENT_FEE,
          AmendmentsBilling.JPM_WILL_COVER_FEE,
          AmendmentsBilling.COMPEX_AMENDMENT_FEE,
          AmendmentsBilling.INTERNAL_MERGER_FEE,
          AmendmentsBilling.EXTERNAL_MERGER_FEE
        ]
      : [
          AmendmentsBilling.WAIVED___FEE_INCLUDED_IN_CONVERSION,
          AmendmentsBilling.WAIVED___ADDRESS_CHANGE,
          AmendmentsBilling.WAIVED___CLIENT_OR_FA_RELATIONSHIP,
          AmendmentsBilling.WAIVED___OTHER__ADD_TO_DESCRIPTION,
          AmendmentsBilling.WAIVED___TPA_SUBMISSION,
          AmendmentsBilling.WAIVED___LOAN_POLICY_CHANGE,
          AmendmentsBilling.WAIVED___VW_ERROR
        ];
  }, [isBillable]);

  const planSearch = useQuery(
    ['amendmentsPlanSearch', debouncedSearchTerm],
    () =>
      GlobalSearchService.getGlobalSearchResult(
        debouncedSearchTerm,
        1,
        10,
        GLOBAL_SEARCH_RESULT_PLANS_TYPE
      ),
    {
      enabled: Boolean(debouncedSearchTerm)
    }
  );

  const createAmendment = useMutation(
    ['AmendmentService.createAmendment', plan?.sponsorPlanId],
    (data: CreateAmendmentsRecordDto) =>
      AmendmentsService.createAmendment(plan.sponsorPlanId, data),
    {
      onError: () => {
        snackbar.showSnackbar({
          message: `Unable to create amendment for plan ${plan?.sponsorPlanId}`,
          severity: 'error'
        });
      },
      onSuccess: async () => {
        const filterQueryKey = props.updatedFilters.length
          ? props.updatedFilters
          : defaultFilters;

        await queryClient.invalidateQueries([
          'AmendmentService.getAmendments',
          AmendmentStatus.NEW_REQUESTS,
          filterQueryKey
        ]);
        snackbar.showSnackbar({
          message: `Successfully created amendment for plan ${plan?.sponsorPlanId}`,
          severity: 'success'
        });
        props.close();
      }
    }
  );

  const handleSubmit = (values: CreateAmendmentsRecordDto) => {
    const payload = {
      ...values,
      billing: values.billing,
      provisions: values.provisions,
      status: AmendmentStatus.NEW_REQUESTS,
      isSubmitted: false,
      adminStatus: plan?.adminStatus
    };

    createAmendment.mutate(payload);
  };

  const form = useMemo(() => {
    return (
      <Formik
        initialValues={{
          planId: '',
          planName: '',
          provisions: [],
          changeRequestNotes: {
            currentProvision: '',
            requestedProvision: '',
            notes: '',
            intentionOfChange: '',
            affectedParty: ''
          },
          desiredEffectiveDate: '',
          isPsaChangeNeeded: false,
          billable: '',
          billing: '',
          isFeeWaiverCompleted: false,
          authorizedSigner: {
            name: '',
            phone: '',
            email: ''
          },
          advisor: {
            name: '',
            phone: '',
            email: ''
          },
          adminStatus: '',
          isSubmitted: false,
          productTierId: null,
          firmId: null
        }}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange
        validateOnMount
        validationSchema={createAmendmentValidationSchema}>
        {formProps => {
          return (
            <Form>
              <DialogTitle>New Amendment</DialogTitle>
              <DialogContent>
                <Grid container rowSpacing={2}>
                  <Grid
                    border={1}
                    borderColor='grey.400'
                    borderRadius={1}
                    xs={12}>
                    <Grid
                      container
                      direction='row'
                      justifyContent='flex-end'
                      spacing={0}>
                      <Grid margin={2} xs={12}>
                        <Typography fontSize={14}>
                          When using this form, please note:
                          <br />
                          <Typography
                            fontSize={14}
                            sx={
                              isTextExpanded
                                ? { marginLeft: '20px' }
                                : copyStyling
                            }>
                            Amendment requests should be submitted after all
                            information is received in good order, and the
                            request is actionable. If you have questions on the
                            feasibility of an amendment, you should first
                            consult the Amendment and Conversion Guidelines for
                            more information. If you do not have all information
                            required below, or you still have questions on the
                            feasibility of an amendment, please communicate with
                            the Document Compliance Team via #plandocuments
                            (Slack) to resolve prior to submitting the request.
                            If you need an amendment with two or more effective
                            dates, you must fill out individual forms for each
                            request as these are separate amendments.
                          </Typography>
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        direction='column'
                        justifyContent='flex-end'
                        xs={2}>
                        <Button
                          onClick={() => {
                            toggleIsTextExpanded();
                          }}
                          variant='text'>
                          {isTextExpanded ? 'Read Less' : 'Read More'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid marginTop={2} xs={12}>
                    {!hasSelectedPlan ? (
                      <FormControl
                        error={Boolean(formProps.errors['planId'])}
                        fullWidth>
                        <Autocomplete
                          data-testid='plan-search-autocomplete'
                          filterOptions={options => options}
                          filterSelectedOptions
                          getOptionLabel={option => option.planName || ''}
                          isOptionEqualToValue={(option, value) =>
                            option?.sponsorPlanId === value?.sponsorPlanId
                          }
                          loading={planSearch.isLoading}
                          onChange={(e, value) => {
                            setPlan(value);
                            toggleHasSelectedPlan();
                            formProps.setFieldValue(
                              'planId',
                              value.sponsorPlanId
                            );
                            formProps.setFieldValue('planName', value.planName);
                            formProps.setFieldTouched('planId', true);
                          }}
                          options={planSearch.data?.data?.plans?.data || []}
                          renderInput={params => {
                            return (
                              <TextField
                                {...params}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: planSearch.isFetching ? (
                                    <CircularProgress
                                      color='primary'
                                      disableShrink
                                      size={16}
                                      variant='indeterminate'
                                    />
                                  ) : (
                                    <InputAdornment
                                      position='end'
                                      sx={{
                                        color: formProps.errors['planId']
                                          ? theme.palette.error.main
                                          : 'inherit'
                                      }}>
                                      <Search />
                                    </InputAdornment>
                                  ),
                                  placeholder: 'Search plan by name or ID'
                                }}
                                data-component='autoCompleteInput'
                                error={Boolean(formProps.errors['planId'])}
                                fullWidth
                                name='planId'
                                onChange={e => setSearchTerm(e.target.value)}
                                ref={params.InputProps.ref}
                                size='small'
                                sx={{
                                  '& .MuiInputBase-input::placeholder': {
                                    color: formProps.errors['planId']
                                      ? theme.palette.error.main
                                      : 'inherit',
                                    opacity: 1
                                  }
                                }}
                                value={searchTerm}
                              />
                            );
                          }}
                          renderOption={(params, option) => (
                            <PlanInputSearch
                              data-testid='plan-input-search'
                              key={option.sponsorPlanId}
                              parentParams={params}
                              plan={option}
                              searchTerm={searchTerm}
                              setSearchTerm={setSearchTerm}
                            />
                          )}
                          size='small'
                          value={plan}
                        />
                      </FormControl>
                    ) : (
                      <SelectedPlanCard
                        selectedPlan={plan}
                        setFieldValue={formProps.setFieldValue}
                        setPlan={setPlan}
                        toggle={toggleHasSelectedPlan}
                      />
                    )}
                    {formProps.errors['planId'] && (
                      <Typography color='error' fontSize={12} paddingTop={1}>
                        {formProps.errors['planId']}
                      </Typography>
                    )}
                  </Grid>

                  <Grid marginTop={1}>
                    <FormGroup>
                      <FormLabel>Provisions (Select all that apply)</FormLabel>
                      {formProps.errors.provisions && (
                        <Typography color='error' fontSize={12}>
                          {formProps.errors.provisions}
                        </Typography>
                      )}
                      <Grid container marginLeft={3} rowSpacing={1}>
                        {Object.values(AmendmentsProvisions).map(
                          (provision, index) => (
                            <Grid key={index} xs={6}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onChange={(_, value) => {
                                      if (!value) {
                                        const removedProvision =
                                          formProps.values.provisions.filter(
                                            p => p !== provision
                                          );
                                        formProps.setFieldValue(
                                          'provisions',
                                          removedProvision
                                        );
                                      } else {
                                        formProps.setFieldValue('provisions', [
                                          ...formProps.values.provisions,
                                          provision
                                        ]);
                                      }
                                    }}
                                  />
                                }
                                key={index}
                                label={provision}
                                name='provisions'
                              />
                            </Grid>
                          )
                        )}
                      </Grid>
                    </FormGroup>
                  </Grid>

                  <Grid marginTop={2} xs={12}>
                    <FormControl
                      error={Boolean(formProps.errors.desiredEffectiveDate)}
                      fullWidth
                      size='small'>
                      <Field
                        as={DatePicker}
                        data-testid='desired-effective-date-field'
                        minDate={dayjs(new Date())}
                        name='desiredEffectiveDate'
                        placeholder='Desired Effective Date'
                        sx={{
                          '& input::placeholder': {
                            color: formProps.errors.desiredEffectiveDate
                              ? theme.palette.error.main
                              : 'inherit',
                            opacity: 1
                          }
                        }}
                        variant='outlined'
                      />
                    </FormControl>
                    <Typography color='grey.700' fontSize={12} marginTop={1}>
                      One effective date only. If there are multiple effective
                      dates, please submit more than one amendment request.
                    </Typography>
                  </Grid>

                  <Grid container direction='column' marginTop={2} xs={12}>
                    <Grid>
                      <Divider textAlign='left'>Change Requested</Divider>
                    </Grid>

                    <Grid
                      columnSpacing={2}
                      container
                      justifyContent='space-between'>
                      <Grid xs={4}>
                        <FormControl fullWidth>
                          <TextField
                            data-testid='current-provision-field'
                            defaultValue=''
                            fullWidth
                            id='current-provision-text'
                            label='Current Provision'
                            multiline
                            name='currentProvision'
                            onChange={e =>
                              formProps.setFieldValue(
                                'changeRequestNotes.currentProvision',
                                e.target.value
                              )
                            }
                            rows={4}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={4}>
                        <FormControl fullWidth>
                          <TextField
                            data-testid='requested-provision-field'
                            defaultValue=''
                            fullWidth
                            id='requested-provision-text'
                            label='Requested Provision'
                            multiline
                            name='requestedProvision'
                            onChange={e =>
                              formProps.setFieldValue(
                                'changeRequestNotes.requestedProvision',
                                e.target.value
                              )
                            }
                            rows={4}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={4}>
                        <FormControl fullWidth>
                          <TextField
                            data-testid='notes-field'
                            defaultValue=''
                            fullWidth
                            id='notes-text'
                            label='Notes'
                            multiline
                            name='notes'
                            onChange={e =>
                              formProps.setFieldValue(
                                'changeRequestNotes.notes',
                                e.target.value
                              )
                            }
                            rows={4}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Grid container rowSpacing={2}>
                      <Grid xs={12}>
                        <FormControl fullWidth>
                          <TextField
                            data-testid='intention-of-change-field'
                            defaultValue=''
                            fullWidth
                            id='intentionOfChange'
                            name='intentionOfChange'
                            onChange={e =>
                              formProps.setFieldValue(
                                'changeRequestNotes.intentionOfChange',
                                e.target.value
                              )
                            }
                            placeholder='What is the intention for this plan provision change?'
                            size='small'
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12}>
                        <FormControl fullWidth>
                          <TextField
                            data-testid='affected-party-field'
                            defaultValue=''
                            fullWidth
                            id='affectedParty'
                            name='affectedParty'
                            onChange={e =>
                              formProps.setFieldValue(
                                'changeRequestNotes.affectedParty',
                                e.target.value
                              )
                            }
                            placeholder='Who will this amendment apply to (eg. all current participants)?'
                            size='small'
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid xs={12}>
                    <FormControlLabel
                      control={<Checkbox />}
                      data-testid='psa-change-checkbox'
                      label='PSA Change Needed'
                      name='isPsaChangeNeeded'
                      onChange={(e, value) =>
                        formProps.setFieldValue('isPsaChangeNeeded', value)
                      }
                    />
                    <Typography color='grey.700' fontSize={12}>
                      If a PSA is required, please be sure to submit a separate
                      request form to Deal Desk.
                    </Typography>
                  </Grid>

                  <Grid container marginTop={2} xs={12}>
                    <Divider textAlign='left'>Billing</Divider>
                    <Grid container direction='row' spacing={3} xs={12}>
                      <Grid xs={4}>
                        <FormControl
                          error={Boolean(formProps.errors['billable'])}
                          fullWidth
                          size='small'>
                          <InputLabel id='new-amendment-billable-select'>
                            Billable
                          </InputLabel>
                          <Select
                            data-testid='billable-select'
                            fullWidth
                            id='new-amendment-billable-select'
                            label='Billable'
                            name='billable'
                            onChange={e => {
                              formProps.setFieldValue(
                                'billable',
                                e.target.value
                              );
                              setIsBillable(e.target.value === 'true');
                            }}
                            placeholder='Billable'
                            value={formProps.values['billable']}>
                            <MenuItem value='true'>Yes - Billable</MenuItem>
                            <MenuItem value='false'>No - Not Billable</MenuItem>
                          </Select>
                        </FormControl>
                        {formProps.errors.billing && (
                          <Typography
                            color='error'
                            fontSize={12}
                            paddingTop={1}>
                            {formProps.errors.billing}
                          </Typography>
                        )}
                      </Grid>

                      <Grid xs={8}>
                        <FormControl
                          error={
                            isBillable === null
                              ? null
                              : Boolean(formProps.errors.billing)
                          }
                          fullWidth
                          size='small'>
                          <InputLabel id='new-amendment-amount-select'>
                            Amount
                          </InputLabel>
                          <Select
                            data-testid='billable-amount-select'
                            disabled={isBillable === null}
                            fullWidth
                            id='new-amendment-billable-amt-select'
                            label='Amount'
                            name='billing'
                            onChange={e => {
                              formProps.setFieldValue(
                                'billing',
                                e.target.value
                              );
                            }}
                            placeholder='Amount'
                            value={formProps.values.billing}>
                            {billableAmountSelections.map(amt => (
                              <MenuItem key={amt} value={amt}>
                                {amt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid xs={12}>
                    <FormControlLabel
                      control={<Checkbox />}
                      data-testid='fee-waiver-checkbox'
                      label='Fee waiver form completed'
                      name='isFeeWaiverCompleted'
                      onChange={(e, value) =>
                        formProps.setFieldValue('isFeeWaiverCompleted', value)
                      }
                    />
                  </Grid>

                  <Grid
                    columnGap={1}
                    container
                    justifyContent='flex-end'
                    xs={12}>
                    <DialogActions>
                      <Grid>
                        <Button
                          color='primary'
                          onClick={() => {
                            props.close();
                            formProps.resetForm();
                          }}
                          variant='outlined'>
                          Cancel
                        </Button>
                      </Grid>
                      <Grid>
                        <Button
                          color='primary'
                          data-testid='create-amendment-button'
                          disabled={
                            !formProps.isValid || createAmendment.isLoading
                          }
                          type='submit'
                          variant='contained'>
                          Create
                          {/* Save if edit */}
                        </Button>
                      </Grid>
                    </DialogActions>
                  </Grid>
                </Grid>
              </DialogContent>
            </Form>
          );
        }}
      </Formik>
    );
  }, [isTextExpanded, planSearch.isLoading, plan, hasSelectedPlan, isBillable]);

  return <>{form}</>;
};
